import { useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import {
  CloseFullscreenRounded,
  CloseRounded,
  CloudDownloadOutlined,
  ContentCopyOutlined,
  DeleteOutline,
  EditOutlined,
  OpenInFullRounded,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { ApiService, FileModel } from 'api';
import { DEFAULT_DOC_CONTENT_STALE_TIME_IN_MS } from 'components/Drawer/document-preview-drawer-tab/document-preview.constants';
import { useXlsxSpreadsheetNotesQuery } from 'components/Drawer/document-preview-drawer-tab/xlsx-viewer/useXlsxSpreadsheetNotesQuery';
import { XLSX_VIEWER_STRINGS } from 'components/Drawer/document-preview-drawer-tab/xlsx-viewer/xlsx-viewer.contants';
import ImageViewer from 'components/FileViewer/image-viewer/ImageViewer';
import PDFDocumentSearch from 'components/FileViewer/pdf-viewer/pdf-document-search/PDFDocumentSearch';
import { SearchMatch } from 'components/FileViewer/pdf-viewer/pdf-document-search/PDFDocumentSearch.props';
import { PageData } from 'components/FileViewer/pdf-viewer/pdf-utils/constants';
import PDFViewer from 'components/FileViewer/pdf-viewer/PDFViewer';
import { PDFViewerViewMode, SearchHighlight } from 'components/FileViewer/pdf-viewer/PDFViewer.props';
import SpreadsheetWorkbookPreview from 'components/FileViewer/spreadsheet-viewer/SpreadsheetWorkbookPreview';
import {
  FIRM_DOCUMENT_XLSX_WORKBOOK_PREVIEW_FEATURE_OPTIONS,
  getSpreadsheetNoteDisplayOptionsFromPreviewFeatures,
} from 'components/FileViewer/spreadsheet-viewer/SpreadsheetWorkbookPreview.options';
import { useXlsxPresentationOverrides } from 'components/FileViewer/spreadsheet-viewer/useXlsxPresentationOverrides';
import { isFindKeyboardShortcut, isScopedViewerKeyboardShortcut } from 'components/FileViewer/viewerKeyboard.utils';
import useSpreadsheetNoteTabs from 'components/SpreadsheetNotes/SpreadsheetNoteConfig/UseSpreadsheetNoteTabs/useSpreadsheetNoteTabs';
import { applyXlsxPresentationOverridesToNotes } from 'components/SpreadsheetNotes/utilities/xlsxPresentationOverrides.utils';
import type { FileRowAction } from 'components/ui';
import { FileTypeIcon, OverflowTooltip } from 'components/ui';
import { useZoom } from 'context/ZoomContext';
import { FIRM_DOCUMENT_FILE_ACTIONS_LABELS } from 'pages/Documents/FirmDocuments/components/FirmDocumentFileActions/FirmDocumentFileActions.constants';
import { getFirmDocumentReferenceFileName } from 'pages/Documents/FirmDocuments/utilities';
import { useDocuments } from 'services/hooks';
import { Logger } from 'utilities';
import type { FirmDocumentPreviewPanelProps } from './FirmDocumentPreviewPanel.props';
import {
  FirmDocumentPreviewPanelActions,
  FirmDocumentPreviewPanelBody,
  FirmDocumentPreviewPanelCard,
  FirmDocumentPreviewPanelCloseButton,
  FirmDocumentPreviewPanelHeader,
  FirmDocumentPreviewPanelHeaderButton,
  FirmDocumentPreviewPanelLoader,
  FirmDocumentPreviewPanelMetadata,
  FirmDocumentPreviewPanelMetadataChip,
  FirmDocumentPreviewPanelMetadataChipText,
  FirmDocumentPreviewPanelPdfViewer,
  FirmDocumentPreviewPanelRoot,
  FirmDocumentPreviewPanelStateAction,
  FirmDocumentPreviewPanelStateDescription,
  FirmDocumentPreviewPanelStateIcon,
  FirmDocumentPreviewPanelStateTitle,
  FirmDocumentPreviewPanelTextPreview,
  FirmDocumentPreviewPanelTitle,
  FirmDocumentPreviewPanelViewer,
} from './FirmDocumentPreviewPanel.styles';
import { FIRM_DOCUMENT_PREVIEW_PANEL_TEST_IDS } from './FirmDocumentPreviewPanel.testIds';
import type { FirmDocumentPreviewContentResponse } from './FirmDocumentPreviewPanel.types';
import {
  getPreviewMetadataItems,
  isFirmDocumentImagePreviewFileType,
  isFirmDocumentPreviewableFileType,
  isFirmDocumentSpreadsheetPreviewFileType,
  isFirmDocumentTextPreviewFileType,
} from './FirmDocumentPreviewPanel.utils';

const decodeBase64TextContent = (content: string) => {
  try {
    const binaryString = atob(content);
    const bytes = Uint8Array.from(binaryString, character => character.charCodeAt(0));

    return new TextDecoder().decode(bytes);
  } catch {
    return content;
  }
};

const SIGNED_URL_PREVIEW_STALE_TIME_IN_MS = 1000 * 60 * 5;
const FIRM_DOCUMENT_XLSX_PREVIEW_DISPLAY_OPTIONS = getSpreadsheetNoteDisplayOptionsFromPreviewFeatures(
  FIRM_DOCUMENT_XLSX_WORKBOOK_PREVIEW_FEATURE_OPTIONS
);

const FIRM_DOCUMENT_PDF_PARSER_RESULTS_QUERY_KEY = 'firm-document-pdf-parser-results';
const FIRM_DOCUMENT_PDF_TOOLBAR_HEIGHT = 49;

const fetchFirmDocumentPdfParserJson = async (documentReferenceId: number): Promise<PageData[]> => {
  try {
    const parserResultsData = await ApiService.apiDocumentsReferencesParserResultsRead(String(documentReferenceId));

    if (!parserResultsData.url) {
      return [];
    }

    const response = await fetch(parserResultsData.url);
    return response.json() as Promise<PageData[]>;
  } catch (error) {
    Logger.error('Failed to load PDF parser results for Firm Documents preview:', error);
    return [];
  }
};

const FirmDocumentPreviewMetadataChipItem = ({
  icon,
  label,
  testId,
  value,
}: {
  icon: ReactNode;
  label: string;
  testId: string;
  value: string;
}) => {
  const labelRef = useRef<HTMLSpanElement | null>(null);
  const chipLabel = `${label}: ${value}`;

  return (
    <OverflowTooltip measureRef={labelRef} title={chipLabel}>
      <FirmDocumentPreviewPanelMetadataChip data-testid={testId}>
        {icon}
        <FirmDocumentPreviewPanelMetadataChipText ref={labelRef}>{value}</FirmDocumentPreviewPanelMetadataChipText>
      </FirmDocumentPreviewPanelMetadataChip>
    </OverflowTooltip>
  );
};

const FirmDocumentPreviewState = ({
  actionDisabled = false,
  actionLoading = false,
  actionTestId,
  description,
  documentReference,
  onActionClick,
  title,
}: {
  actionDisabled?: boolean;
  actionLoading?: boolean;
  actionTestId?: string;
  description: string;
  documentReference: FirmDocumentPreviewPanelProps['documentReference'];
  onActionClick?: () => void;
  title: string;
}) => (
  <FirmDocumentPreviewPanelCard>
    <FirmDocumentPreviewPanelStateIcon>
      <FileTypeIcon fileType={documentReference.file.file_type} />
    </FirmDocumentPreviewPanelStateIcon>
    <FirmDocumentPreviewPanelStateTitle>{title}</FirmDocumentPreviewPanelStateTitle>
    <FirmDocumentPreviewPanelStateDescription>{description}</FirmDocumentPreviewPanelStateDescription>
    {onActionClick ? (
      <FirmDocumentPreviewPanelStateAction
        data-testid={actionTestId}
        disabled={actionDisabled}
        loading={actionLoading}
        loadingBehavior="inline"
        onClick={(event: MouseEvent<HTMLButtonElement>) => {
          event.stopPropagation();
          onActionClick();
        }}
        sizeVariant="compact"
        startIcon={<CloudDownloadOutlined fontSize="inherit" />}
        type="button"
        visualVariant="brand">
        Download
      </FirmDocumentPreviewPanelStateAction>
    ) : null}
  </FirmDocumentPreviewPanelCard>
);

const FirmDocumentPreviewLoadingState = ({
  documentReference,
}: {
  documentReference: FirmDocumentPreviewPanelProps['documentReference'];
}) => (
  <FirmDocumentPreviewPanelCard aria-busy="true" aria-live="polite">
    <FirmDocumentPreviewPanelStateIcon>
      <FileTypeIcon fileType={documentReference.file.file_type} />
    </FirmDocumentPreviewPanelStateIcon>
    <FirmDocumentPreviewPanelStateTitle>Loading preview</FirmDocumentPreviewPanelStateTitle>
    <FirmDocumentPreviewPanelStateDescription>
      Preparing {getFirmDocumentReferenceFileName(documentReference)}
    </FirmDocumentPreviewPanelStateDescription>
    <FirmDocumentPreviewPanelLoader />
  </FirmDocumentPreviewPanelCard>
);

const FirmDocumentSpreadsheetPreview = ({
  base64Content,
  documentReference,
  fallback,
  isPresentationSourceLoading = false,
  sourceUrl,
}: {
  base64Content?: string;
  documentReference: FirmDocumentPreviewPanelProps['documentReference'];
  fallback: ReactNode;
  isPresentationSourceLoading?: boolean;
  sourceUrl?: string;
}) => {
  const { data, error, isLoading, isPolling } = useXlsxSpreadsheetNotesQuery(documentReference.file.id);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [availableViewerHeight, setAvailableViewerHeight] = useState(600);

  // Reset workbook-local UI state when users select a different preview document.
  useEffect(() => {
    setActiveTabIndex(0);
  }, [documentReference.document_reference_id]);

  const { presentationOverrides, isLoading: isPresentationOverridesLoading } = useXlsxPresentationOverrides({
    base64Content,
    fileId: documentReference.file.id ?? documentReference.document_reference_id,
    isSourceLoading: isPresentationSourceLoading,
    sourceUrl,
  });
  const isPreviewLoading = ((isLoading || isPresentationOverridesLoading) && !error && !isPolling) || isPolling;

  const notes = useMemo(() => data ?? [], [data]);
  const notesWithPresentationOverrides = useMemo(
    () => applyXlsxPresentationOverridesToNotes(notes, presentationOverrides),
    [notes, presentationOverrides]
  );
  const { tabs, workbookContextValue } = useSpreadsheetNoteTabs({
    notes: notesWithPresentationOverrides,
    actionsAreDisabled: false,
    isAttributeMapperEnabled: false,
    documentId: documentReference.document_reference_id,
    displayOptions: FIRM_DOCUMENT_XLSX_PREVIEW_DISPLAY_OPTIONS,
    parentContainerHeight: availableViewerHeight,
  });

  const viewerAriaLabel = `${getFirmDocumentReferenceFileName(documentReference)} spreadsheet preview`;

  return (
    <FirmDocumentPreviewPanelViewer>
      <SpreadsheetWorkbookPreview
        activeTabIndex={activeTabIndex}
        ariaLabel={viewerAriaLabel}
        error={error || (!tabs.length && !isPreviewLoading)}
        fallback={fallback}
        featureOptions={FIRM_DOCUMENT_XLSX_WORKBOOK_PREVIEW_FEATURE_OPTIONS}
        isLoading={isPreviewLoading}
        loadingMessage={isPolling || isPresentationOverridesLoading ? XLSX_VIEWER_STRINGS.LOADING.MESSAGE : undefined}
        onActiveTabIndexChange={setActiveTabIndex}
        onViewerHeightChange={setAvailableViewerHeight}
        resetKey={documentReference.document_reference_id}
        tabValuePrefix="firm-document-spreadsheet-preview-sheet"
        tabs={tabs}
        testId="firm-document-spreadsheet-preview-workbook"
        workbookContextValue={workbookContextValue}
      />
    </FirmDocumentPreviewPanelViewer>
  );
};

const FirmDocumentPdfPreview = ({
  base64Content,
  documentReference,
  isSourceLoading,
  sourceUrl,
}: {
  base64Content?: string;
  documentReference: FirmDocumentPreviewPanelProps['documentReference'];
  isSourceLoading: boolean;
  sourceUrl?: string;
}) => {
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchHighlight, setSearchHighlight] = useState<SearchHighlight | null>(null);
  const searchEnabled = Boolean(documentReference.file.json_file_path);
  const { data: jsonData = [] } = useQuery<PageData[]>({
    enabled: searchEnabled && Boolean(documentReference.document_reference_id),
    queryFn: () => fetchFirmDocumentPdfParserJson(documentReference.document_reference_id),
    queryKey: [
      FIRM_DOCUMENT_PDF_PARSER_RESULTS_QUERY_KEY,
      documentReference.document_reference_id,
      documentReference.file.json_file_path,
    ],
    staleTime: DEFAULT_DOC_CONTENT_STALE_TIME_IN_MS,
  });
  const hasJsonData = jsonData.length > 0;

  // Reset search state when the preview switches to another PDF.
  useEffect(() => {
    setSearchOpen(false);
    setSearchHighlight(null);
  }, [documentReference.document_reference_id]);

  // Match the drawer PDF search shortcut for citable Firm Documents PDFs.
  useEffect(() => {
    if (!hasJsonData) {
      return;
    }

    const previewElement = previewRef.current;
    const targetWindow = previewElement?.ownerDocument.defaultView;
    if (!previewElement || !targetWindow) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isFindKeyboardShortcut(event) || !isScopedViewerKeyboardShortcut(previewElement, event)) {
        return;
      }

      event.preventDefault();
      setSearchOpen(prevOpen => !prevOpen);
    };

    targetWindow.addEventListener('keydown', handleKeyDown);
    return () => targetWindow.removeEventListener('keydown', handleKeyDown);
  }, [hasJsonData]);

  const handleSearchMatchSelect = (match: SearchMatch) => {
    setSearchHighlight({
      pageNumber: match.pageNumber,
      rects: match.rects,
      wordIds: match.matchedWordIds,
    });
  };

  const handleSearchClose = () => {
    setSearchOpen(false);
    setSearchHighlight(null);
  };

  const handleSearchToggle = () => {
    if (searchOpen) {
      handleSearchClose();
      return;
    }

    setSearchOpen(true);
  };

  return (
    <FirmDocumentPreviewPanelViewer ref={previewRef}>
      <FirmDocumentPreviewPanelPdfViewer>
        {searchOpen && hasJsonData ? (
          <PDFDocumentSearch
            compensateAppZoom={false}
            density="compact"
            jsonData={jsonData}
            onClose={handleSearchClose}
            onMatchSelect={handleSearchMatchSelect}
            topOffset={FIRM_DOCUMENT_PDF_TOOLBAR_HEIGHT}
          />
        ) : null}
        <PDFViewer
          allowNativeTextSelection
          base64Content={base64Content}
          deferResizeUpdates
          isSourceLoading={isSourceLoading}
          jsonData={hasJsonData ? jsonData : undefined}
          onSearchToggle={hasJsonData ? handleSearchToggle : undefined}
          pageChrome="floating"
          searchHighlight={searchHighlight}
          searchOpen={searchOpen}
          src={sourceUrl}
          viewMode={PDFViewerViewMode.DOCUMENT}
        />
      </FirmDocumentPreviewPanelPdfViewer>
    </FirmDocumentPreviewPanelViewer>
  );
};

const FirmDocumentPreviewContent = ({
  actionInProgress = false,
  canDownloadDocuments = true,
  documentReference,
  downloadingDocumentReferenceId = null,
  firmId = null,
  onDocumentDownload,
}: {
  actionInProgress?: FirmDocumentPreviewPanelProps['actionInProgress'];
  canDownloadDocuments?: FirmDocumentPreviewPanelProps['canDownloadDocuments'];
  documentReference: FirmDocumentPreviewPanelProps['documentReference'];
  downloadingDocumentReferenceId?: FirmDocumentPreviewPanelProps['downloadingDocumentReferenceId'];
  firmId?: FirmDocumentPreviewPanelProps['firmId'];
  onDocumentDownload?: FirmDocumentPreviewPanelProps['onDocumentDownload'];
}) => {
  const { downloadDocument, downloadDocumentContent } = useDocuments();
  const fileType = documentReference.file.file_type;
  const isDownloadInProgress = downloadingDocumentReferenceId === documentReference.document_reference_id;
  const isDownloadUnavailable =
    actionInProgress || !canDownloadDocuments || isDownloadInProgress || !onDocumentDownload;
  const isPreviewableFileType = isFirmDocumentPreviewableFileType(fileType);
  const isImagePreview = isFirmDocumentImagePreviewFileType(fileType);
  const isSpreadsheetPreview = isFirmDocumentSpreadsheetPreviewFileType(fileType);
  const isTextPreview = isFirmDocumentTextPreviewFileType(fileType);
  const isPdfPreview = fileType === FileModel.file_type.PDF;
  const previewDocumentReference = useMemo(
    () => (firmId != null ? { ...documentReference, firmId } : documentReference),
    [documentReference, firmId]
  );
  const shouldUseSignedUrlPreview = isImagePreview;
  const shouldFallbackToSignedUrl = isImagePreview || isSpreadsheetPreview;
  const { data, isLoading } = useQuery<FirmDocumentPreviewContentResponse>({
    enabled: isPreviewableFileType && Boolean(documentReference.document_reference_id),
    queryFn: async () => {
      if (shouldUseSignedUrlPreview) {
        const downloadResponse = await downloadDocument(previewDocumentReference, false, {
          showErrorNotification: false,
        });

        if (downloadResponse.signed_url) {
          return {
            blob_url: downloadResponse.signed_url,
            signed_url: downloadResponse.signed_url,
          };
        }
      }

      return downloadDocumentContent(previewDocumentReference, {
        fallbackToSignedUrl: shouldFallbackToSignedUrl,
        showErrorNotification: false,
      }) as Promise<FirmDocumentPreviewContentResponse>;
    },
    queryKey: [
      'firm-document-preview',
      shouldUseSignedUrlPreview ? 'signed-url-first' : 'content-first',
      documentReference.document_reference_id,
      documentReference.file.id,
      firmId,
      fileType,
    ],
    staleTime: shouldUseSignedUrlPreview ? SIGNED_URL_PREVIEW_STALE_TIME_IN_MS : DEFAULT_DOC_CONTENT_STALE_TIME_IN_MS,
  });
  const textContent = useMemo(() => {
    if (!isTextPreview || !data?.content) {
      return null;
    }

    return decodeBase64TextContent(data.content);
  }, [data?.content, isTextPreview]);
  const unavailablePreviewState = (
    <FirmDocumentPreviewState
      actionDisabled={isDownloadUnavailable}
      actionLoading={isDownloadInProgress}
      actionTestId={FIRM_DOCUMENT_PREVIEW_PANEL_TEST_IDS.downloadFallbackAction}
      description="This preview could not be loaded."
      documentReference={documentReference}
      onActionClick={onDocumentDownload ? () => onDocumentDownload(documentReference) : undefined}
      title="Preview unavailable"
    />
  );

  if (!isPreviewableFileType) {
    return (
      <FirmDocumentPreviewState
        actionDisabled={isDownloadUnavailable}
        actionLoading={isDownloadInProgress}
        actionTestId={FIRM_DOCUMENT_PREVIEW_PANEL_TEST_IDS.downloadFallbackAction}
        description="This file type is not supported for preview."
        documentReference={documentReference}
        onActionClick={onDocumentDownload ? () => onDocumentDownload(documentReference) : undefined}
        title="Preview not supported"
      />
    );
  }

  if (isPdfPreview) {
    if (!isLoading && !data?.content && !data?.blob_url) {
      return unavailablePreviewState;
    }

    return (
      <FirmDocumentPdfPreview
        base64Content={data?.content ?? undefined}
        documentReference={documentReference}
        isSourceLoading={isLoading}
        sourceUrl={data?.blob_url}
      />
    );
  }

  if (isImagePreview) {
    if (!isLoading && !data?.content && !data?.blob_url) {
      return unavailablePreviewState;
    }

    return (
      <FirmDocumentPreviewPanelViewer>
        <ImageViewer
          alt={getFirmDocumentReferenceFileName(documentReference)}
          animateOnMount={false}
          base64Content={data?.content ?? undefined}
          contentType={data?.content_type ?? undefined}
          isSourceLoading={isLoading}
          src={data?.blob_url}
        />
      </FirmDocumentPreviewPanelViewer>
    );
  }

  if (isSpreadsheetPreview) {
    return (
      <FirmDocumentSpreadsheetPreview
        base64Content={data?.content ?? undefined}
        documentReference={documentReference}
        fallback={unavailablePreviewState}
        isPresentationSourceLoading={isLoading}
        sourceUrl={data?.blob_url}
      />
    );
  }

  if (isLoading) {
    return <FirmDocumentPreviewLoadingState documentReference={documentReference} />;
  }

  if (!data?.content && !data?.blob_url) {
    return unavailablePreviewState;
  }

  if (isTextPreview && textContent != null) {
    return <FirmDocumentPreviewPanelTextPreview>{textContent}</FirmDocumentPreviewPanelTextPreview>;
  }

  return (
    <FirmDocumentPreviewState
      actionDisabled={isDownloadUnavailable}
      actionLoading={isDownloadInProgress}
      actionTestId={FIRM_DOCUMENT_PREVIEW_PANEL_TEST_IDS.downloadFallbackAction}
      description="This file type is not supported for preview."
      documentReference={documentReference}
      onActionClick={onDocumentDownload ? () => onDocumentDownload(documentReference) : undefined}
      title="Preview not supported"
    />
  );
};

const FirmDocumentPreviewPanel = ({
  actionInProgress = false,
  canCopyDocuments = false,
  canDeleteDocuments = true,
  canDownloadDocuments = true,
  canRenameDocuments = true,
  documentReference,
  downloadingDocumentReferenceId = null,
  firmId = null,
  isExpanded = false,
  onDocumentCopy,
  onDocumentDelete,
  onDocumentDownload,
  onDocumentRename,
  onPreviewExpandToggle,
  onPreviewClose,
}: FirmDocumentPreviewPanelProps) => {
  const { zoomLevel } = useZoom();
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const fileName = getFirmDocumentReferenceFileName(documentReference);
  const fullscreenPreviewLabel = isExpanded ? 'Exit fullscreen preview' : 'Open fullscreen preview';
  const isDownloadInProgress = downloadingDocumentReferenceId === documentReference.document_reference_id;
  const metadataItems = useMemo(() => getPreviewMetadataItems(documentReference), [documentReference]);
  const actions = useMemo<FileRowAction[]>(() => {
    const isDisabled = actionInProgress || !documentReference.file.id;
    const isDownloadDisabled = isDisabled || !canDownloadDocuments || isDownloadInProgress || !onDocumentDownload;

    return [
      {
        ariaLabel: FIRM_DOCUMENT_FILE_ACTIONS_LABELS.download,
        disabled: isDownloadDisabled,
        icon: <CloudDownloadOutlined fontSize="inherit" />,
        id: 'download',
        isLoading: isDownloadInProgress,
        onClick: event => {
          event.stopPropagation();
          onDocumentDownload?.(documentReference);
        },
        testId: FIRM_DOCUMENT_PREVIEW_PANEL_TEST_IDS.downloadAction,
        tooltipLabel: FIRM_DOCUMENT_FILE_ACTIONS_LABELS.download,
      },
      ...(onDocumentCopy
        ? [
            {
              ariaLabel: FIRM_DOCUMENT_FILE_ACTIONS_LABELS.copy,
              disabled: isDisabled || !canCopyDocuments,
              icon: <ContentCopyOutlined fontSize="inherit" />,
              id: 'copy',
              onClick: event => {
                event.stopPropagation();
                onDocumentCopy(documentReference);
              },
              testId: FIRM_DOCUMENT_PREVIEW_PANEL_TEST_IDS.copyAction,
              tooltipLabel: FIRM_DOCUMENT_FILE_ACTIONS_LABELS.copy,
            } satisfies FileRowAction,
        ]
        : []),
      {
        ariaLabel: FIRM_DOCUMENT_FILE_ACTIONS_LABELS.rename,
        disabled: isDisabled || !canRenameDocuments || !onDocumentRename,
        icon: <EditOutlined fontSize="inherit" />,
        id: 'rename',
        onClick: event => {
          event.stopPropagation();
          onDocumentRename?.(documentReference);
        },
        testId: FIRM_DOCUMENT_PREVIEW_PANEL_TEST_IDS.renameAction,
        tooltipLabel: FIRM_DOCUMENT_FILE_ACTIONS_LABELS.rename,
      },
      {
        ariaLabel: FIRM_DOCUMENT_FILE_ACTIONS_LABELS.delete,
        disabled: isDisabled || !canDeleteDocuments || !onDocumentDelete,
        icon: <DeleteOutline fontSize="inherit" />,
        id: 'delete',
        onClick: event => {
          event.stopPropagation();
          onDocumentDelete?.(documentReference);
        },
        testId: FIRM_DOCUMENT_PREVIEW_PANEL_TEST_IDS.deleteAction,
        tone: 'danger',
        tooltipLabel: FIRM_DOCUMENT_FILE_ACTIONS_LABELS.delete,
      },
    ];
  }, [
    actionInProgress,
    canCopyDocuments,
    canDeleteDocuments,
    canDownloadDocuments,
    canRenameDocuments,
    documentReference,
    isDownloadInProgress,
    onDocumentCopy,
    onDocumentDelete,
    onDocumentDownload,
    onDocumentRename,
  ]);

  return (
    <FirmDocumentPreviewPanelRoot
      aria-label="Document preview"
      data-testid={FIRM_DOCUMENT_PREVIEW_PANEL_TEST_IDS.root}
      isExpanded={isExpanded}>
      <FirmDocumentPreviewPanelHeader>
        <OverflowTooltip measureRef={titleRef} title={fileName}>
          <FirmDocumentPreviewPanelTitle ref={titleRef} data-testid={FIRM_DOCUMENT_PREVIEW_PANEL_TEST_IDS.title}>
            {fileName}
          </FirmDocumentPreviewPanelTitle>
        </OverflowTooltip>
        {metadataItems.length ? (
          <FirmDocumentPreviewPanelMetadata data-testid={FIRM_DOCUMENT_PREVIEW_PANEL_TEST_IDS.metadata}>
            {metadataItems.map(item => (
              <FirmDocumentPreviewMetadataChipItem
                key={item.key}
                icon={item.icon}
                label={item.label}
                testId={FIRM_DOCUMENT_PREVIEW_PANEL_TEST_IDS.metadataChip(item.key)}
                value={item.value}
              />
            ))}
          </FirmDocumentPreviewPanelMetadata>
        ) : null}
        <FirmDocumentPreviewPanelActions
          actions={actions}
          ariaLabel={FIRM_DOCUMENT_FILE_ACTIONS_LABELS.trigger(fileName)}
          testId={FIRM_DOCUMENT_PREVIEW_PANEL_TEST_IDS.actions}
          zoomLevel={zoomLevel}
        />
        {onPreviewExpandToggle ? (
          <FirmDocumentPreviewPanelHeaderButton
            aria-label={fullscreenPreviewLabel}
            data-testid={FIRM_DOCUMENT_PREVIEW_PANEL_TEST_IDS.expandAction}
            onClick={event => {
              event.stopPropagation();
              onPreviewExpandToggle();
            }}
            size="small"
            tooltipLabel={fullscreenPreviewLabel}
            variant="ghost"
            visualSize="compact">
            {isExpanded ? <CloseFullscreenRounded fontSize="inherit" /> : <OpenInFullRounded fontSize="inherit" />}
          </FirmDocumentPreviewPanelHeaderButton>
        ) : null}
        {!isExpanded ? (
          <FirmDocumentPreviewPanelCloseButton
            aria-label="Close preview"
            data-testid={FIRM_DOCUMENT_PREVIEW_PANEL_TEST_IDS.closeAction}
            onClick={event => {
              event.stopPropagation();
              onPreviewClose();
            }}
            size="small"
            tooltipLabel="Close preview"
            variant="ghost"
            visualSize="compact">
            <CloseRounded fontSize="inherit" />
          </FirmDocumentPreviewPanelCloseButton>
        ) : null}
      </FirmDocumentPreviewPanelHeader>
      <FirmDocumentPreviewPanelBody data-testid={FIRM_DOCUMENT_PREVIEW_PANEL_TEST_IDS.body}>
        <FirmDocumentPreviewContent
          actionInProgress={actionInProgress}
          canDownloadDocuments={canDownloadDocuments}
          documentReference={documentReference}
          downloadingDocumentReferenceId={downloadingDocumentReferenceId}
          firmId={firmId}
          onDocumentDownload={onDocumentDownload}
        />
      </FirmDocumentPreviewPanelBody>
    </FirmDocumentPreviewPanelRoot>
  );
};

export default FirmDocumentPreviewPanel;
